import React, { useState } from "react";

import {
  Box,
  Button,
  Dialog,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Theme,
  Typography
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { fetchQuote } from "../../store/QuoteSlice";
import { mailPaymentLink, mailQuoteToUser } from "../../api/SwyftApi";
import { ETemplateType, Account, PageState, Template, EQuoteType } from "../../api/Types";
import { Quote } from "../../api/Quote/Quote";
import { colors, Loader } from "swyft-react-core";
import { toast } from "react-toastify";
import ClearIcon from "@material-ui/icons/Clear";
import { RootState } from "../../app/rootReducer";
import { useEffect } from "react";
import { fetchTemplates } from "../../store/TemplateSlice";
import { useConfig } from "../../components/config/ConfigContext";
import { FeaturePaymentBalance } from "../../app/consts";
import { EQuoteStatus } from "../../api/Quote/EQuoteStatus";

const useStyles = makeStyles((theme: Theme) => ({
  margin: {
    margin: theme.spacing(1)
  },
  input: {
    width: "100%"
  },
  button: {
    margin: theme.spacing(1)
  },
  templateSelect: {
    "minWidth": 250,
    "color": colors.primary,
    "& h3": {
      "padding": theme.spacing(1),
      "paddingLeft": theme.spacing(3),
      "marginBottom": theme.spacing(1),
      "&:not(.head):not(.selected)": {
        cursor: "pointer"
      }
    },
    "& .head": {
      color: colors.white,
      backgroundColor: colors.primary
    },
    "& .selected": {
      color: colors.secondary,
      borderRight: `5px solid ${colors.secondary}`
    }
  },
  title: {
    "padding": theme.spacing(2),
    "marginLeft": 250 + theme.spacing(2),
    "display": "flex",
    '& [class*="Button-label svg"]': {
      marginRight: theme.spacing(2)
    },
    "& button": {
      "marginLeft": theme.spacing(2),
      "&:disabled svg": {
        fill: colors.lightGrey,
        stroke: colors.lightGrey
      }
    }
  },
  main: {
    padding: theme.spacing(2)
  },
  fields: {
    "& tr": {
      "& td": {
        "borderBottom": 0,
        "&:first-of-type": {
          width: 100
        }
      }
    }
  },
  content: {
    marginTop: theme.spacing(2),
    border: "1px solid black",
    overflowY: "scroll",
    height: 400
  },
  actions: {
    "display": "flex",
    "justifyContent": "flex-end",
    "marginTop": theme.spacing(2),
    "& button": {
      minWidth: "10em",
      marginLeft: theme.spacing(3)
    }
  },
  commentDialog: {
    minWidth: 600,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column"
  },
  confirmDeleteDialog: {
    minWidth: 600,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column"
  }
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
  quote: Quote;
  account: Account;
}

interface State {
  emailAddress: string;
  name: string;
  template?: Template;
  commentOpen: boolean;
  comfirmDeleteOpen: boolean;
  commentInputText: string;
  commentActiveText: string;
  file: File | null;
  paymentAmount?: number | null;
}

export const EmailModal: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { isFeatureEnabled } = useConfig();

  const { quote, account } = props;
  const customer = quote.customer!;

  const { templates, pageState, error } = useSelector((state: RootState) => state.template);

  const nameForTemplate = account.firstName || account.name || customer.name || "";

  const [state, setState] = useState<State>({
    emailAddress: customer.email || account.email || "",
    name: nameForTemplate,
    template: undefined,
    commentOpen: false,
    comfirmDeleteOpen: false,
    commentInputText: "",
    commentActiveText: "",
    file: null,
    paymentAmount: 0
  });

  useEffect(() => {
    if (!templates) {
      dispatch(fetchTemplates());
    }
  }, [templates, dispatch]);

  useEffect(() => {
    setState((s) => {
      return {
        ...s,
        template: undefined
      };
    });
  }, [quote]);

  if (!templates || pageState === PageState.Loading) {
    return (
      <Dialog
        open={props.isOpen}
        onClose={props.onClose}
        aria-labelledby="template-selection"
        aria-describedby="template-select"
        fullWidth={true}
        maxWidth="xl"
      >
        <Loader />
      </Dialog>
    );
  }

  if (error) {
    console.log(error);
    return <div>Error</div>;
  }

  const filterTemplates = templates
    .filter((t) => {
      if (t.type === ETemplateType.Payment) {
        return true;
      }
      switch (quote.type) {
        case EQuoteType.Boiler:
          return t.type === ETemplateType.Boiler;
        case EQuoteType.Solar:
          return t.type === ETemplateType.Solar;
        case EQuoteType.BoilerSolar:
          return t.type === ETemplateType.BoilerSolar;
      }
      return true;
    })
    .sort((a, b) => (a.type === ETemplateType.Payment ? 1 : b.type === ETemplateType.Payment ? -1 : 0));

  if (state.template === undefined) {
    setState({
      ...state,
      template: filterTemplates[0]
    });
  }

  const handleSend = async () => {
    if (state.emailAddress === "") {
      toast.error(`Enter a valid email address`);
      return;
    }

    if (state.template?.type === ETemplateType.Payment) {
      await mailPaymentLink(quote.id, state.emailAddress, state.name, state.template!.id, 0)
        .then(() => {
          toast.success(`Email sent to ${state.emailAddress}`);
          props.onClose();
        })
        .catch((err) => {
          console.log(err);
          toast.error(err);
        });
    } else {
      await mailQuoteToUser(
        quote.id,
        state.emailAddress,
        state.name,
        state.commentActiveText,
        state.template!.id,
        state.file
      )
        .then(() => {
          toast.success(`Email sent to ${state.emailAddress}`);
          props.onClose();
          setState({
            ...state,
            file: null
          });
          dispatch(fetchQuote(quote.id));
        })
        .catch((err) => {
          console.log(err);
          toast.error(err);
        });
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files === null) {
      return;
    }
    setState({
      ...state,
      file: e.currentTarget.files[0]
    });
  };

  const setSelectedTemplate = (type: ETemplateType) => {
    var template = templates.filter((t) => t.type === type)[0];
    setState({ ...state, template });
  };

  return (
    <React.Fragment>
      <Dialog
        open={props.isOpen}
        onClose={props.onClose}
        aria-labelledby="template-selection"
        aria-describedby="template-select"
        fullWidth={true}
        maxWidth="xl"
      >
        <Box className={classes.title}>
          <Box flexGrow={1} mt={"12px"}>
            <Typography variant="h2">Send Customer Email</Typography>
          </Box>
          <IconButton
            onClick={(_) => {
              setState({
                ...state,
                file: null
              });
              props.onClose();
            }}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        <Box display="flex">
          <Box className={classes.templateSelect}>
            <Typography variant="h3" className="head">
              Email Templates
            </Typography>
            {filterTemplates.map((t) => (
              <Box key={t.type} onClick={() => setSelectedTemplate(t.type)}>
                <Typography
                  variant="h3"
                  className={t.type === (state.template && state.template.type) ? "selected" : ""}
                >
                  {t.name}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box flexGrow={1} className={classes.main}>
            <Table className={classes.fields} size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">To</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" component="div">
                      <TextField
                        label="Email Address"
                        variant="outlined"
                        size="small"
                        value={state.emailAddress}
                        onChange={(e) => setState({ ...state, emailAddress: e.target.value })}
                        className={classes.input}
                      />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">From</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">hi@swyftenergy.ie</Typography>
                  </TableCell>
                </TableRow>
                {state.template?.subject && (
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Subject</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">{state.template.subject}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {/* {state.template?.type === ETemplateType.Payment && (
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Amount</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" component="div">
                        <TextField
                          label="Amount"
                          variant="outlined"
                          size="small"
                          value={state.paymentAmount}
                          onChange={(e) => setState({ ...state, paymentAmount: parseFloat(e.target.value) || 0 })}
                          className={classes.input}
                          type="number"
                        />
                      </Typography>
                    </TableCell>
                  </TableRow>
                )} */}
              </TableBody>
            </Table>

            <Box px={2}>
              {state.template?.type === ETemplateType.Payment && !isFeatureEnabled(FeaturePaymentBalance) && (
                <Box mb={2} color={colors.secondary}>
                  <Typography variant="body1">Payment link sending currently disabled.</Typography>
                </Box>
              )}
              <Box>
                {state.template && state.template?.type !== ETemplateType.Payment && (
                  <TextField
                    fullWidth
                    label="Comment"
                    name="commentActiveText"
                    onBlur={(e) => setState({ ...state, commentActiveText: e.target.value })}
                    onChange={(e) => setState({ ...state, commentActiveText: e.target.value })}
                    value={state.commentActiveText}
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>

            <Box className={classes.actions}>
              {state.template?.type !== ETemplateType.Payment && (
                <Box display="flex" flexGrow={1} mx={2}>
                  <input
                    // accept="pdf/*"
                    className={classes.input}
                    style={{ display: "none" }}
                    id="raised-button-file"
                    onChange={handleFile}
                    type="file"
                  />
                  <label htmlFor="raised-button-file">
                    <Button variant="contained" component="span" className={classes.button}>
                      Attach file
                    </Button>
                  </label>
                  {state.file && (
                    <Box pt={2} fontWeight="fontWeightBold">
                      <Typography variant="body1">{state.file.name}</Typography>
                    </Box>
                  )}
                </Box>
              )}
              <Button
                variant="contained"
                color="secondary"
                onClick={(_) => handleSend()}
                className={classes.button}
                disabled={
                  (!isFeatureEnabled(FeaturePaymentBalance) ||
                    (quote.status !== EQuoteStatus.Approved && quote.status !== EQuoteStatus.Payment)) &&
                  state.template?.type === ETemplateType.Payment
                }
              >
                Send
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={(_) => {
                  setState({
                    ...state,
                    file: null
                  });
                  props.onClose();
                }}
                className={classes.button}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </React.Fragment>
  );
};
